import React, { useState, useRef, useEffect, useCallback } from 'react';
import './HandoffMarker.css';

interface HandoffMarkerProps {
  /** Texto da anotação que aparece ao clicar no marker */
  note: React.ReactNode;
  /** Posição dentro do parent (parent precisa ser position: relative) */
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
  /** Tamanho do marker em px (diâmetro). Default: 16. */
  size?: number;
  /** Posição do popover em relação ao marker. Default: 'top'. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Rótulo acessível. Default: "Ver anotação de design". */
  ariaLabel?: string;
  /** Quando true, usa position: fixed (relativo à viewport). Útil pra ancorar em cima de elementos em drawers/modais. */
  fixed?: boolean;
}

export function HandoffMarker({
  note,
  top,
  right,
  bottom,
  left,
  size = 16,
  placement = 'top',
  ariaLabel = 'Ver anotação de design',
  fixed = false,
}: HandoffMarkerProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Fecha ao apertar ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  const wrapperStyle: React.CSSProperties = {
    top,
    right,
    bottom,
    left,
    ...(fixed ? { position: 'fixed' } : null),
  };
  const dotStyle: React.CSSProperties = { width: size, height: size };

  return (
    <div
      ref={wrapperRef}
      className="handoff-marker"
      style={wrapperStyle}
    >
      <button
        type="button"
        className="handoff-marker-dot"
        style={dotStyle}
        onClick={toggle}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span className="handoff-marker-pulse" />
      </button>

      {open && (
        <div
          className={`handoff-marker-popover handoff-marker-popover--${placement}`}
          role="dialog"
        >
          {typeof note === 'string' ? <p>{note}</p> : note}
        </div>
      )}
    </div>
  );
}
